import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { useIntl } from "@ai360/core/intl";
import { HierarchyAPI, SteeringDataAPI } from "@ai360/core";
import { getTheUserGuid } from "~/login/selectors";

import {
    Section,
    SubSection,
    TextInput,
    TextArea,
    RadioButtonGroup,
    RadioButton,
    Loader,
    LoaderTypes,
} from "~/core";

import { UpArrowIcon } from "~/core/icons";

import { OrgLevelList } from "../org-level-products/add-edit/components/org-level-list";

import { steeringDataActions, steeringDataSelectors } from "./data";
import { messages } from "./i18n-messages";

import "./steering-data-form.css";
import "../add-edit-shared-responsive.css";

// --- Constants ---

const SUPPORTED_EXTENSIONS = [
    ".pdf",
    ".doc",
    ".docx",
    ".txt",
    ".csv",
    ".xls",
    ".xlsx",
    ".pptx",
    ".zip",
    ".jpg",
    ".jpeg",
    ".png",
];

const NAME_MAX_LENGTH = 255;
const NOTES_MAX_LENGTH = 2000;
const URL_MAX_LENGTH = 2048;

type SourceType = "file" | "url";

type IHierarchyFilterLevelWithParentsGuid = HierarchyAPI.IHierarchyFilterLevelWithParentsGuid;

// --- URL Validation ---

const PRIVATE_URL_PATTERNS = [/sharepoint\.com/i, /atlassian\.net/i, /confluence/i, /jira/i];

const PRIVATE_IP_PATTERNS = [
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}/,
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}/,
    /^192\.168\.\d{1,3}\.\d{1,3}/,
];

function isWellFormedUrl(value: string): boolean {
    if (!value || value.length > URL_MAX_LENGTH) {
        return false;
    }
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

function isPrivateUrl(value: string): boolean {
    try {
        const url = new URL(value);
        const hostname = url.hostname;

        // Check hostname patterns
        for (const pattern of PRIVATE_URL_PATTERNS) {
            if (pattern.test(hostname)) {
                return true;
            }
        }

        // Check private IP patterns
        for (const pattern of PRIVATE_IP_PATTERNS) {
            if (pattern.test(hostname)) {
                return true;
            }
        }

        return false;
    } catch {
        return false;
    }
}

function validateUrl(value: string): string | null {
    if (!value) {
        return "Public URL is required.";
    }
    if (value.length > URL_MAX_LENGTH) {
        return `URL must not exceed ${URL_MAX_LENGTH} characters.`;
    }
    if (!isWellFormedUrl(value)) {
        return "Please enter a valid URL with http:// or https:// scheme.";
    }
    if (isPrivateUrl(value)) {
        return "Only public URLs are supported. Private URLs (SharePoint, Jira, Confluence, internal IPs) are not allowed.";
    }
    return null;
}

// --- Component Props ---

interface ISteeringDataFormProps {
    closePanel: () => void;
    selectedRecord: SteeringDataAPI.ISteeringDataRecord | null;
}

// --- Component ---

export const SteeringDataForm = ({ closePanel, selectedRecord }: ISteeringDataFormProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const userGuid = useAppSelector(getTheUserGuid);
    const loading = useAppSelector(steeringDataSelectors.getSteeringDataLoading);

    const isEditMode = !!selectedRecord;

    // --- Form State ---

    const [name, setName] = useState<string>(selectedRecord?.name || "");
    const [sourceType, setSourceType] = useState<SourceType>(selectedRecord?.sourceType || "file");
    const [publicUrl, setPublicUrl] = useState<string>(selectedRecord?.publicUrl || "");
    const [notes, setNotes] = useState<string>(selectedRecord?.notes || "");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    // Hierarchy levels
    const [orgLevels, setOrgLevels] = useState<IHierarchyFilterLevelWithParentsGuid[]>([]);
    const [selectedOrgLevels, setSelectedOrgLevels] = useState<
        IHierarchyFilterLevelWithParentsGuid[]
    >([]);
    const [orgLevelsLoading, setOrgLevelsLoading] = useState<boolean>(true);

    // Validation errors
    const [nameError, setNameError] = useState<string | null>(null);
    const [urlError, setUrlError] = useState<string | null>(null);
    const [hierarchyError, setHierarchyError] = useState<string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

    // Track submission state
    const [_isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Ref for drag-and-drop container
    const dndContainerRef = useRef<HTMLDivElement>(null);
    const fileSelectorInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);

    // --- Load Org Levels ---

    useEffect(() => {
        let cancelled = false;

        const loadOrgLevels = async () => {
            try {
                setOrgLevelsLoading(true);
                const response = await HierarchyAPI.getOrgLevelList(userGuid);
                if (!cancelled) {
                    setOrgLevels(response || []);
                    setOrgLevelsLoading(false);
                }
            } catch {
                if (!cancelled) {
                    setOrgLevels([]);
                    setOrgLevelsLoading(false);
                }
            }
        };

        loadOrgLevels();
        return () => {
            cancelled = true;
        };
    }, [userGuid]);

    // --- Initialize selected org levels from existing record ---

    useEffect(() => {
        if (isEditMode && selectedRecord?.hierarchyLevels && orgLevels.length > 0) {
            const selectedGuids = new Set(
                selectedRecord.hierarchyLevels.map((level) => level.orgLevelGuid)
            );
            const matched = orgLevels.filter((level) => selectedGuids.has(level.orgLevelGuid));
            setSelectedOrgLevels(matched);
        }
    }, [isEditMode, selectedRecord, orgLevels]);

    // --- Drag and Drop File Handling ---

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            const file = files[0];
            const ext = file.name.match(/\.[^.]+$/)?.[0]?.toLowerCase();
            if (ext && SUPPORTED_EXTENSIONS.includes(ext)) {
                setSelectedFile(file);
                setFileError(null);
            } else {
                setFileError(
                    `Unsupported file format. Supported: ${SUPPORTED_EXTENSIONS.join(", ")}`
                );
            }
        }
    }, []);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const ext = file.name.match(/\.[^.]+$/)?.[0]?.toLowerCase();
            if (ext && SUPPORTED_EXTENSIONS.includes(ext)) {
                setSelectedFile(file);
                setFileError(null);
            } else {
                setFileError(
                    `Unsupported file format. Supported: ${SUPPORTED_EXTENSIONS.join(", ")}`
                );
            }
        }
        // Reset input so the same file can be re-selected if needed
        if (fileSelectorInputRef.current) {
            fileSelectorInputRef.current.value = "";
        }
    }, []);

    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null);
    }, []);

    // --- Validation ---

    const validateForm = useCallback((): boolean => {
        let valid = true;

        // Name validation
        if (!name.trim()) {
            setNameError("Name is required.");
            valid = false;
        } else if (name.trim().length > NAME_MAX_LENGTH) {
            setNameError(`Name must not exceed ${NAME_MAX_LENGTH} characters.`);
            valid = false;
        } else {
            setNameError(null);
        }

        // Source type validation
        if (sourceType === "url") {
            const urlErr = validateUrl(publicUrl);
            if (urlErr) {
                setUrlError(urlErr);
                valid = false;
            } else {
                setUrlError(null);
            }
        } else {
            setUrlError(null);
            // File validation: required on add mode
            if (!isEditMode && !selectedFile) {
                setFileError("Please select a file to upload.");
                valid = false;
            } else {
                setFileError(null);
            }
        }

        // Hierarchy levels validation
        if (selectedOrgLevels.length === 0) {
            setHierarchyError("At least one hierarchy level must be selected.");
            valid = false;
        } else {
            setHierarchyError(null);
        }

        return valid;
    }, [name, sourceType, publicUrl, selectedFile, selectedOrgLevels, isEditMode]);

    // --- Submit ---

    const onSubmit = useCallback(async () => {
        setSubmitError(null);
        setSubmitSuccess(false);

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        const hierarchyLevelGuids = selectedOrgLevels.map((level) => level.orgLevelGuid);

        try {
            if (isEditMode) {
                dispatch(
                    steeringDataActions.updateSteeringData({
                        steeringDataGuid: selectedRecord.steeringDataGuid,
                        name: name.trim(),
                        sourceType,
                        publicUrl: sourceType === "url" ? publicUrl : undefined,
                        hierarchyLevelGuids,
                        notes: notes.trim() || undefined,
                        file: sourceType === "file" ? selectedFile : undefined,
                    })
                );
            } else {
                dispatch(
                    steeringDataActions.createSteeringData({
                        name: name.trim(),
                        sourceType,
                        publicUrl: sourceType === "url" ? publicUrl : undefined,
                        hierarchyLevelGuids,
                        notes: notes.trim() || undefined,
                        file: sourceType === "file" ? selectedFile : undefined,
                    })
                );
            }

            setSubmitSuccess(true);
            setIsSubmitting(false);

            // Close panel after brief delay to show success
            setTimeout(() => {
                closePanel();
            }, 1000);
        } catch (error) {
            setSubmitError(error?.message || "An error occurred while saving. Please try again.");
            setIsSubmitting(false);
            // Form data is preserved for retry
        }
    }, [
        validateForm,
        isEditMode,
        selectedRecord,
        name,
        sourceType,
        publicUrl,
        notes,
        selectedFile,
        selectedOrgLevels,
        dispatch,
        closePanel,
    ]);

    // --- Listen for submit event from SlidingPanel header button ---

    useEffect(() => {
        const handleExternalSubmit = () => {
            onSubmit();
        };
        document.addEventListener("steering-data-form-submit", handleExternalSubmit);
        return () => {
            document.removeEventListener("steering-data-form-submit", handleExternalSubmit);
        };
    }, [onSubmit]);

    // --- Hierarchy Level Selection ---

    const onOrgLevelSelectionChange = useCallback(
        (items: IHierarchyFilterLevelWithParentsGuid[]) => {
            setSelectedOrgLevels(items);
            if (items.length > 0) {
                setHierarchyError(null);
            }
        },
        []
    );

    // --- Render ---

    return (
        <div className="add-edit-panel steering-data-add-edit-panel">
            {loading && <Loader type={LoaderTypes.LINE_SCALE} />}

            {submitSuccess && (
                <div className="steering-data-form-success">
                    {isEditMode
                        ? formatMessage(messages.updateSuccess)
                        : formatMessage(messages.createSuccess)}
                </div>
            )}

            {submitError && <div className="steering-data-form-error">{submitError}</div>}

            <div className="section-container">
                {/* Left Column: Source type toggle, Name, URL/Notes */}
                <Section>
                    <SubSection>
                        <RadioButtonGroup
                            className="steering-data-source-toggle"
                            selectedValue={sourceType}
                            disabled={isEditMode}
                            afterOnChange={(value: string | boolean) => {
                                if (!isEditMode) {
                                    setSourceType(value as SourceType);
                                    setUrlError(null);
                                    setFileError(null);
                                }
                            }}
                        >
                            <RadioButton
                                tabIndex={0}
                                value="file"
                                label={formatMessage(messages.fileUploadLabel)}
                                disabled={isEditMode}
                            />
                            <RadioButton
                                tabIndex={0}
                                value="url"
                                label={formatMessage(messages.publicUrlLabel)}
                                disabled={isEditMode}
                            />
                        </RadioButtonGroup>
                        {isEditMode && (
                            <span className="steering-data-locked-hint">
                                {formatMessage(messages.sourceTypeLocked)}
                            </span>
                        )}
                    </SubSection>
                    <SubSection>
                        <TextInput
                            required
                            autoFocus
                            containerClassNames={["form-long-input"]}
                            maxLength={NAME_MAX_LENGTH}
                            placeholderText={formatMessage(messages.nameLabel)}
                            value={name}
                            onChange={(value: string) => {
                                setName(value);
                                if (nameError) setNameError(null);
                            }}
                            hasError={!!nameError}
                            errorText={nameError}
                        />
                    </SubSection>
                    {sourceType === "url" && (
                        <SubSection>
                            <TextInput
                                required
                                containerClassNames={["form-long-input"]}
                                maxLength={URL_MAX_LENGTH}
                                placeholderText={formatMessage(messages.publicUrlLabel)}
                                value={publicUrl}
                                onChange={(value: string) => {
                                    setPublicUrl(value);
                                    if (urlError) setUrlError(null);
                                }}
                                hasError={!!urlError}
                                errorText={urlError}
                            />
                        </SubSection>
                    )}
                    <SubSection>
                        <TextArea
                            maxLength={NOTES_MAX_LENGTH}
                            placeholderText={formatMessage(messages.notesLabel)}
                            value={notes}
                            onChange={(value: string) => setNotes(value)}
                        />
                    </SubSection>
                </Section>

                <span className="bar section-fence" />

                {/* Right Column: Location Affiliations */}
                <Section
                    required
                    className="grid-section"
                    headerText={formatMessage(messages.hierarchyLevelsLabel)}
                >
                    {orgLevelsLoading ? (
                        <Loader type={LoaderTypes.LINE_SCALE} />
                    ) : (
                        <OrgLevelList
                            orgLevels={orgLevels}
                            selectedOrgLevels={selectedOrgLevels}
                            onSelectionChange={onOrgLevelSelectionChange}
                        />
                    )}
                    {hierarchyError && (
                        <div className="steering-data-field-error">{hierarchyError}</div>
                    )}
                </Section>
            </div>

            {/* File Upload Section - below section-container (file mode only) */}
            {sourceType === "file" && (
                <div className="steering-data-file-section">
                    <div
                        className={`steering-data-file-upload-container${
                            isDragOver ? " drag-over" : ""
                        }`}
                        ref={dndContainerRef}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <input
                            ref={fileSelectorInputRef}
                            style={{ display: "none" }}
                            type="file"
                            accept={SUPPORTED_EXTENSIONS.join(",")}
                            onChange={handleFileSelect}
                        />
                        {selectedFile ? (
                            <div className="steering-data-selected-file">
                                <span className="steering-data-selected-file-name">
                                    {selectedFile.name}
                                </span>
                                <button
                                    type="button"
                                    className="steering-data-remove-file-btn"
                                    onClick={handleRemoveFile}
                                    aria-label="Remove file"
                                >
                                    &times;
                                </button>
                            </div>
                        ) : (
                            <div className="steering-data-drop-message">
                                <UpArrowIcon className="steering-data-upload-icon" />
                                <p>Drag Files Here</p>
                                <p>Or</p>
                                <button
                                    type="button"
                                    className="steering-data-browse-btn"
                                    onClick={() => fileSelectorInputRef.current?.click()}
                                >
                                    Click Here To Browse To Files
                                </button>
                            </div>
                        )}
                    </div>
                    {fileError && <div className="steering-data-field-error">{fileError}</div>}
                    {isEditMode && selectedRecord?.fileName && !selectedFile && (
                        <div className="steering-data-current-file">
                            {formatMessage(messages.currentFile, {
                                fileName: selectedRecord.fileName,
                            })}
                        </div>
                    )}
                    <div className="steering-data-supported-badge">
                        Supported File Types:
                        <br />
                        .pdf,.docx,.pptx,.xlsx,.txt,.csv,.zip,.jpg,.png
                    </div>
                </div>
            )}
        </div>
    );
};

export default SteeringDataForm;
